/**
 * O registry PARCIAL do módulo de Comissionamento.
 *
 * `get_relatorio_comissionamento` emite `ui.component: 'comissoes/relatorio'`
 * desde 11/09/2026 (`tools/tool_comissoes.py`, `COMPONENTE`) — e até esta
 * entrada existir a chave caía em `chave_desconhecida`: a aba de IA de
 * Comissões (`views/view-comissoes-ia/`, que monta o MESMO `Messages` do chat
 * geral) desenhava "Não consegui montar este cartão" sobre um relatório que
 * chegou inteiro.
 *
 * ⚠️ O commit que criou o contrato no backend anotou que o cartão "nasce
 * INERTE: nenhum frontend deste monorepo consome o canal `ui` hoje". A
 * premissa valia até agosto; hoje o `UI_REGISTRY` tem nove parciais e ~30
 * cartões renderizados, e esta superfície é uma das quatro que montam partes
 * de turno (§9.1 do `CLAUDE.md` do `aawz-intelligence-v2`). O cartão não era
 * inerte — era um fallback de erro na tela.
 *
 * ## Como cada peça se encaixa
 *
 * | peça | onde mora | por quê |
 * |---|---|---|
 * | `component` | `ia/cartoes/relatorio.js` | embrulho que traduz o `ui.props` em props |
 * | `schema` | `ui-registry/schemas/` | CÓPIA byte a byte do `contratos/` do backend |
 * | `label` | aqui | byte a byte o `LABEL` da tool emissora |
 *
 * ★ O `component` NÃO aponta para o `.vue`: o fio é PT-BR/snake_case
 * (`total_de_linhas`, `ordenado_por`, `janela.data_inicial`) e o componente
 * fala camelCase. Apontar direto deixaria o cartão montado e VAZIO.
 *
 * ## `input-available` desenha esqueleto, `output-error` fica de fora
 *
 * Mesma decisão dos outros parciais de leitura: `input-available` é o estado
 * em que a parte vive enquanto a tool roda e enquanto o veredito do schema não
 * volta — recusá-lo faria um cartão de ERRO piscar no meio de todo turno
 * normal. `output-error` fica fora porque este cartão não sabe desenhar uma
 * falha, e o `fallback-card` diz o que aconteceu; `approval-requested` não se
 * aplica (é leitura).
 *
 * ## Duas decisões que a outra branch escreveu, e valem manter
 *
 * 2. **A flag é a do MÓDULO (`FEATURES.COMISSOES`)** — a mesma que gateia a
 *    rota de comissionamento. O registry é fail-closed nela: escritório que não
 *    contratou vê `fallback-card` com `flag_desligada`, em vez de um número de
 *    comissão que não é dele. (A persona já é gateada por `FEATURES.IA_COMISSOES`
 *    no backend; aqui a régua é sobre o DADO, e vale como defesa em
 *    profundidade.)
 * 3. **Nada entra no `LEGACY_TOOL_TO_UI`.** A tool declara `ui` desde que
 *    nasceu, então a parte já chega com a chave; mapear o nome dela ali faria
 *    as mensagens ANTIGAS (persistidas sem `ui`) resolverem para este cartão com
 *    o `value` da tool no lugar do `ui.props` — outro shape — e a conversa
 *    reaberta trocaria o silêncio de hoje por um `fallback-card` de contrato.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { FEATURES } from '@constants/Text'
import { ROUTE_KEYS } from '@constants/navigation-routes'

/** A chave do módulo no catálogo de navegação. */
const MODULO = ROUTE_KEYS.COMISSOES

/**
 * A flag do MÓDULO — a mesma que gateia a rota (`navigation-routes.js`) e o
 * `PERMISSION` do módulo (`comissoes/index.js`).
 *
 * `resolverEntrada` é fail-closed nela: sem a feature o cartão não monta. Um
 * relatório de comissionamento na conversa de um escritório que não contratou
 * o módulo mostraria dado de coisa que ele não tem.
 */
const FLAG_DO_MODULO = FEATURES.COMISSOES

// Enquanto a tool roda, e quando o resultado chega (ver o cabeçalho).
const ESTADOS_DE_LEITURA = ['input-available', 'output-available']

/**
 * As chaves, no formato `<modulo>/<cartao>` do contrato — exportadas porque
 * quem lê o registry e quem escreve o teste precisam da MESMA string.
 */
export const CHAVES = {
  RELATORIO: 'comissoes/relatorio',
}

export default {
  [CHAVES.RELATORIO]: {
    component: () => import('@modules/comissoes/ia/cartoes/relatorio'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-comissoes-relatorio.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    // Byte a byte o `LABEL` de `tool_comissoes.py` — o badge lê daqui e a
    // moldura lê o `label` que veio na PARTE; textos diferentes fariam os
    // dois discordarem sobre a mesma tool no mesmo turno.
    label: 'Consultando o relatório de comissionamento...',
  },
}
